/********************* Setup *********************/

// tag icon shared by the line item discount chips and the applied code chips
const MINI_CART_TAG_ICON = `<svg class="miniCartIcon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z"></path><circle cx="7" cy="7" r="1.5"></circle></svg>`;

// recommendations are only refetched when the set of products in the cart changes
let miniCartRecommendationsKey = "";

// escape customer-entered text (line item properties, discount codes) before it goes into markup
function miniCartEscape(text) {
    return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

// true when the shipping protection switch is rendered and on
function miniCartProtectionChecked() {
    return document.querySelector("#miniCartBodyProtectionSideSwitchInput")?.checked === true;
}

/********************* Open Cart *********************/

async function openCart(skipFetch = false) {
    // open cart if not already open
    if (document.querySelector("#miniCart:not([open])")) {
        document.querySelector("#miniCart").showModal();
    }
    document.querySelector("body").classList.add("noScroll");

    // reopening mid close cancels the close and slides the content back in
    document.querySelector("#miniCart").classList.remove("closing");

    // start loading animation
    document.querySelector("#miniCartContent").classList.add("loading");

    // product pages call openCart(addedProduct) or openCart(null, groupID), so only an explicit true skips the fetch
    if (skipFetch !== true) {
        cart = await getCart();
    }

    // protection is only added at checkout when the switch is on, so strip any copy already in the cart
    if (cartSettings.protection_variant_id) {
        const updatePayload = {};
        cart.items.forEach(item => {
            if (item.variant_id !== cartSettings.protection_variant_id) {
                return;
            }
            updatePayload[item.key] = 0;
        });

        // re-run once the cart no longer holds protection
        if (Object.keys(updatePayload).length > 0) {
            cart = await updateCart({updates: updatePayload});
            await openCart(true);
            return;
        }
    }

    // compare at prices and selling plans are not part of cart.js
    const cartData = await fetch(`${Shopify.routes.root}cart?view=supplemental-data`)
        .then(response => response.json());

    // stop loading animation
    document.querySelector("#miniCartContent").classList.remove("loading");

    // update cart count in header nav, which the lander templates do not render
    document.querySelectorAll(".headerCart").forEach(element => element.setAttribute("data-count", cart.item_count));

    // update the count next to the title, the empty cart shows the title alone
    let countText = `${cart.item_count} items`;
    if (cart.item_count === 1) {
        countText = "1 item";
    }
    if (cart.item_count === 0) {
        countText = "";
    }
    document.querySelector("#miniCartHeaderHeadingCount").textContent = countText;

    // swap between the empty state and the full cart
    const isEmpty = cart.item_count === 0;
    document.querySelector("#miniCartEmpty").classList.toggle("active", isEmpty);
    document.querySelector("#miniCartBody").classList.toggle("active", !isEmpty);

    // the empty cart footer is just the shipping note, so it goes when there is no note
    const footer = document.querySelector("#miniCartFooter");
    footer.classList.toggle("active", !isEmpty || Boolean(document.querySelector("#miniCartFooterNote")));
    footer.classList.toggle("empty", isEmpty);
    if (isEmpty) {
        return;
    }

    // render every area from the fresh cart
    renderMiniCartItems(cartData);
    renderMiniCartDiscounts();
    renderMiniCartTotals(cartData);

    // show the confirmation line under the protection copy while it is switched on
    document.querySelector("#miniCartBodyProtectionTextAdded")?.classList.toggle("active", miniCartProtectionChecked());

    // mobile slims the protection card down to one row once there are two or more line items
    document.querySelector("#miniCartBodyProtection")?.classList.toggle("compact", cart.items.length > 1);

    // recommendations load on their own so the cart is usable while they fetch
    renderMiniCartRecommendations();
}

/********************* Line Items *********************/

// there are 3 types of compare at prices (selling plan, line item discount, compare at price), the greatest one wins
function miniCartComparePrice(item, supplementData) {
    const comparePrices = [item.original_line_price];

    // selling plan compare at price
    if (item.selling_plan_allocation?.compare_at_price) {
        comparePrices.push(item.selling_plan_allocation.compare_at_price * item.quantity);
    }

    // variant compare at price
    if (supplementData.compare_at_price) {
        comparePrices.push(supplementData.compare_at_price * item.quantity);
    }

    return Math.max(...comparePrices);
}

function renderMiniCartItems(cartData) {
    let html = "";

    cart.items.forEach(item => {
        const supplementData = cartData[item.key] || {};
        const title = miniCartEscape(item.product_title);
        const comparePrice = miniCartComparePrice(item, supplementData);

        // image, left as an empty tile when the product has none
        let imageHTML = "";
        if (item.image) {
            imageHTML = `<img class="miniCartBodyListItemImageImg" src="${item.image}&width=200" alt="" width="92" height="92">`;
        }

        // single option products read "Size: Queen", multi option products read "Textured White / 1.5m x 2m"
        let variantHTML = "";
        if (!item.product_has_only_default_variant) {
            let variantText = item.variant_title;
            if (item.options_with_values.length === 1) {
                variantText = `${item.options_with_values[0].name}: ${item.options_with_values[0].value}`;
            }
            variantHTML = `<span class="miniCartBodyListItemInfoTopTextVariant">${miniCartEscape(variantText)}</span>`;
        }

        // visible properties, skipping private ones like the bundle _group_id
        let propertiesItemsHTML = "";
        Object.entries(item.properties || {}).forEach(([key, value]) => {
            if (key.startsWith("_") || !value) {
                return;
            }

            // links (uploads, previews) show as the property name
            if (typeof value === "string" && value.startsWith("https://")) {
                propertiesItemsHTML += `<a class="miniCartBodyListItemInfoTopTextPropertiesLink" href="${miniCartEscape(value)}" target="_blank" rel="noopener">${miniCartEscape(key)}</a>`;
                return;
            }
            propertiesItemsHTML += `<span class="miniCartBodyListItemInfoTopTextPropertiesItem">${miniCartEscape(key)}: ${miniCartEscape(value)}</span>`;
        });
        let propertiesHTML = "";
        if (propertiesItemsHTML) {
            propertiesHTML = `<div class="miniCartBodyListItemInfoTopTextProperties">${propertiesItemsHTML}</div>`;
        }

        // subscription plan name
        let planHTML = "";
        if (item.selling_plan_allocation) {
            planHTML = `<span class="miniCartBodyListItemInfoTopTextPlan">${miniCartEscape(item.selling_plan_allocation.selling_plan.name)}</span>`;
        }

        // cart.js only counts line level discounts, so the supplemental data adds this line's share of order level ones
        let discounts = item.line_level_discount_allocations.map(allocation => ({
            ...allocation.discount_application,
            amount: allocation.amount
        }));
        let finalLinePrice = item.final_line_price;
        if (supplementData.discounts) {
            discounts = supplementData.discounts;
            finalLinePrice = item.original_line_price;
            discounts.forEach(discount => {
                finalLinePrice -= discount.amount;
            });
        }

        // struck through price when anything is discounting the line
        let compareHTML = "";
        if (comparePrice > finalLinePrice) {
            compareHTML = `<s class="miniCartBodyListItemInfoTopPriceCompare">${price(comparePrice)}</s>`;
        }

        // one chip per discount applied to this line
        const discountTitles = [];
        discounts.forEach(discount => {
            const discountTitle = miniCartDiscountTitle(discount);
            if (!discountTitle || discountTitles.includes(discountTitle)) {
                return;
            }
            discountTitles.push(discountTitle);
        });
        let discountsTagsHTML = "";
        discountTitles.forEach(discountTitle => {
            discountsTagsHTML += `<span class="miniCartBodyListItemInfoDiscountsTag">${MINI_CART_TAG_ICON}${miniCartEscape(discountTitle)}</span>`;
        });
        let discountsHTML = "";
        if (discountsTagsHTML) {
            discountsHTML = `<div class="miniCartBodyListItemInfoDiscounts">${discountsTagsHTML}</div>`;
        }

        // subscription upgrade for products that offer a selling plan
        let upgradeHTML = "";
        if (!item.selling_plan_allocation && supplementData.selling_plan_id) {
            upgradeHTML = `<button class="miniCartBodyListItemInfoUpgrade" data-plan="${supplementData.selling_plan_id}" type="button">${miniCartEscape(supplementData.selling_plan_name)}</button>`;
        }

        html += `
            <li class="miniCartBodyListItem" data-key="${item.key}">
                <a class="miniCartBodyListItemImage" href="${item.url}" tabindex="-1" aria-hidden="true">${imageHTML}</a>
                <div class="miniCartBodyListItemInfo">
                    <div class="miniCartBodyListItemInfoTop">
                        <div class="miniCartBodyListItemInfoTopText">
                            <a class="miniCartBodyListItemInfoTopTextTitle" href="${item.url}">${title}</a>
                            ${variantHTML}
                            ${propertiesHTML}
                            ${planHTML}
                        </div>
                        <div class="miniCartBodyListItemInfoTopPrice">
                            ${compareHTML}
                            <span class="miniCartBodyListItemInfoTopPriceFinal">${price(finalLinePrice)}</span>
                        </div>
                    </div>
                    ${discountsHTML}
                    <div class="miniCartBodyListItemInfoActions">
                        <div class="miniCartBodyListItemInfoActionsQuantity">
                            <button class="miniCartBodyListItemInfoActionsQuantityMinus" type="button" aria-label="Decrease quantity of ${title}">
                                <svg class="miniCartIcon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"></path></svg>
                            </button>
                            <input class="miniCartBodyListItemInfoActionsQuantityInput" type="text" inputmode="numeric" pattern="[0-9]*" value="${item.quantity}" aria-label="Quantity of ${title}">
                            <button class="miniCartBodyListItemInfoActionsQuantityPlus" type="button" aria-label="Increase quantity of ${title}">
                                <svg class="miniCartIcon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"></path></svg>
                            </button>
                        </div>
                        <button class="miniCartBodyListItemInfoActionsRemove" type="button" aria-label="Remove ${title}">Remove</button>
                    </div>
                    ${upgradeHTML}
                </div>
            </li>
        `;
    });

    document.querySelector("#miniCartBodyList").innerHTML = html;
}

// the gift added alongside a "purchase" line (main-product-v11/v12) leaves the cart with it
// match on _group_id so only the paired gift item is removed, not other gifts
function miniCartGiftKey(key) {
    const item = cart.items.find(cartItem => cartItem.key === key);
    if (item?.properties?._gift_group !== "purchase") {
        return null;
    }

    const giftItem = cart.items.find(cartItem =>
        cartItem.properties?._gift_group === "gift" &&
        cartItem.properties?._group_id === item.properties._group_id
    );
    return giftItem?.key || null;
}

// write a new quantity for one line, 0 removes it along with its gift pair
async function updateCartItem(key, quantity) {
    document.querySelector("#miniCartContent").classList.add("loading");

    const updates = {
        [key]: quantity
    };
    const giftKey = miniCartGiftKey(key);
    if (quantity === 0 && giftKey) {
        updates[giftKey] = 0;
    }

    cart = await updateCart({updates});

    await openCart(true);
}

// typed quantities apply when the input loses focus or enter is pressed
function handleMiniCartQuantityInput(e) {
    const input = e.target.closest(".miniCartBodyListItemInfoActionsQuantityInput");
    if (!input) {
        return;
    }

    // put the saved quantity back when the entry is not a number
    const quantity = parseInt(input.value);
    if (isNaN(quantity) || quantity < 0) {
        input.value = input.defaultValue;
        return;
    }

    updateCartItem(input.closest(".miniCartBodyListItem").dataset.key, quantity);
}

/********************* Discount Codes *********************/

// codes shopify accepted for this cart
function miniCartDiscountCodes() {
    return (cart.discount_codes || [])
        .filter(code => code.applicable)
        .map(code => code.code);
}

// name shown for a discount application
function miniCartDiscountTitle(application) {
    if (application.title) {
        return application.title;
    }

    // codes can come through without a title, and with one code on the cart it can only be that one
    const codes = miniCartDiscountCodes();
    if (application.type === "discount_code" && codes.length === 1) {
        return codes[0];
    }
    return "";
}

// total saved by one code across cart level and line level discounts
function miniCartDiscountAmount(code) {
    const isMatch = application => miniCartDiscountTitle(application).toLowerCase() === code.toLowerCase();
    let amount = 0;

    // order discounts
    cart.cart_level_discount_applications.forEach(application => {
        if (!isMatch(application)) {
            return;
        }
        amount += application.total_allocated_amount;
    });

    // product discounts
    cart.items.forEach(item => {
        item.line_level_discount_allocations.forEach(allocation => {
            if (!isMatch(allocation.discount_application)) {
                return;
            }
            amount += allocation.amount;
        });
    });

    return amount;
}

function renderMiniCartDiscounts() {
    let html = "";

    miniCartDiscountCodes().forEach(code => {
        const safeCode = miniCartEscape(code);

        // free shipping codes have no amount until checkout
        let amountHTML = "";
        const amount = miniCartDiscountAmount(code);
        if (amount > 0) {
            amountHTML = `<span class="miniCartBodyDiscountCodesCodeAmount">−${price(amount)}</span>`;
        }

        html += `
            <div class="miniCartBodyDiscountCodesCode">
                <span class="miniCartBodyDiscountCodesCodeTag">${MINI_CART_TAG_ICON}${safeCode}</span>
                ${amountHTML}
                <button class="miniCartBodyDiscountCodesCodeRemove" type="button" data-code="${safeCode}" aria-label="Remove code ${safeCode}">Remove</button>
            </div>
        `;
    });

    document.querySelector("#miniCartBodyDiscountCodes").innerHTML = html;
}

function showMiniCartDiscountError() {
    document.querySelector("#miniCartBodyDiscountErrorText").textContent = cartSettings.discount_error_text;
    document.querySelector("#miniCartBodyDiscountError").classList.add("active");
    document.querySelector("#miniCartBodyDiscountFormInput").setAttribute("aria-invalid", "true");
}

function hideMiniCartDiscountError() {
    document.querySelector("#miniCartBodyDiscountError").classList.remove("active");
    document.querySelector("#miniCartBodyDiscountFormInput").removeAttribute("aria-invalid");
}

async function applyMiniCartDiscount(e) {
    e.preventDefault();

    const input = document.querySelector("#miniCartBodyDiscountFormInput");
    const code = input.value.trim();
    if (!code) {
        return;
    }
    hideMiniCartDiscountError();

    // nothing to do when the code is already on the cart
    const appliedCodes = miniCartDiscountCodes();
    if (appliedCodes.some(applied => applied.toLowerCase() === code.toLowerCase())) {
        input.value = "";
        return;
    }

    // the discount param replaces every code, so send the existing ones along with the new one
    document.querySelector("#miniCartContent").classList.add("loading");
    cart = await updateCart({discount: [...appliedCodes, code].join(",")});

    // shopify keeps codes it cannot apply, so roll back to the previous codes when the new one did not take
    const newCode = (cart.discount_codes || []).find(entry => entry.code.toLowerCase() === code.toLowerCase());
    if (!newCode || !newCode.applicable) {
        cart = await updateCart({discount: appliedCodes.join(",")});
        await openCart(true);
        showMiniCartDiscountError();
        return;
    }

    // refetch the cart so line prices, totals and savings all reflect the new discount
    input.value = "";
    await openCart();
}

async function removeMiniCartDiscount(code) {
    document.querySelector("#miniCartContent").classList.add("loading");

    // resend every code except the one being removed
    const remainingCodes = miniCartDiscountCodes().filter(applied => applied !== code);
    await updateCart({discount: remainingCodes.join(",")});

    // refetch the cart so every price and total drops the removed discount
    await openCart();
}

/********************* Totals *********************/

function renderMiniCartTotals(cartData) {
    // sum the line compare prices so the struck through total matches the lines
    let compareTotal = 0;
    cart.items.forEach(item => {
        compareTotal += miniCartComparePrice(item, cartData[item.key] || {});
    });

    // total_price already takes order discounts off, protection is added at checkout
    let total = cart.total_price;
    if (miniCartProtectionChecked()) {
        total += cartSettings.protection_price;
        compareTotal += cartSettings.protection_price;
    }

    // only show the compare total and savings pill when something is saved
    const savings = compareTotal - total;
    const compare = document.querySelector("#miniCartFooterTotalPriceRowCompare");
    const savingsPill = document.querySelector("#miniCartFooterTotalPriceSavings");
    compare.textContent = price(compareTotal);
    compare.classList.toggle("active", savings > 0);
    savingsPill.textContent = `You’re saving ${price(savings)}`;
    savingsPill.classList.toggle("active", savings > 0);

    document.querySelector("#miniCartFooterTotalPriceRowFinal").textContent = price(total);
}

/********************* Recommendations *********************/

async function renderMiniCartRecommendations() {
    const section = document.querySelector("#miniCartBodyRecommendations");
    const track = document.querySelector("#miniCartBodyRecommendationsTrack");

    // quantity changes keep the current list
    const key = cart.items.map(item => item.product_id).sort().join(",");
    if (key === miniCartRecommendationsKey) {
        return;
    }
    miniCartRecommendationsKey = key;

    // render this section through the recommendations api, seeded by the most recently added item
    // asks for shopify's max of 10 so there are enough left after cart items are dropped below
    const url = `${Shopify.routes.root}recommendations/products?section_id=theme-mini-cart&product_id=${cart.items[0].product_id}&limit=20&intent=related`;
    const response = await fetch(url)
        .then(response => response.text())
        .catch(() => "");

    // custom picks render ahead of shopify's, so keep the first of each product that is not already in the cart, up to the limit
    const responseTrack = new DOMParser().parseFromString(response, "text/html").querySelector("#miniCartBodyRecommendationsTrack");
    const cartProductIDs = cart.items.map(item => String(item.product_id));
    const shownProductIDs = [];
    let html = "";
    responseTrack?.querySelectorAll(".miniCartBodyRecommendationsTrackCard").forEach(card => {
        const productID = card.dataset.productId;
        if (cartProductIDs.includes(productID) || shownProductIDs.includes(productID) || shownProductIDs.length >= cartSettings.recommendations_limit) {
            return;
        }

        // the set card goes once every piece is in the cart, before that the quick add starts with the missing ones ticked
        const setProductIDs = card.dataset.setProductIds?.split(",") || [];
        if (setProductIDs.length > 0 && setProductIDs.every(setProductID => cartProductIDs.includes(setProductID))) {
            return;
        }
        shownProductIDs.push(productID);
        html += card.outerHTML;
    });
    track.innerHTML = html;
    track.scrollLeft = 0;

    // hide the whole area when shopify has nothing to suggest
    section.classList.toggle("active", track.children.length > 0);
    updateMiniCartRecommendationsNav();
}

// disable the arrows at either end of the track
function updateMiniCartRecommendationsNav() {
    const track = document.querySelector("#miniCartBodyRecommendationsTrack");
    document.querySelector("#miniCartBodyRecommendationsHeaderNavPrev").disabled = track.scrollLeft <= 0;
    document.querySelector("#miniCartBodyRecommendationsHeaderNavNext").disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
}

// move the track one card at a time
function scrollMiniCartRecommendations(direction) {
    const track = document.querySelector("#miniCartBodyRecommendationsTrack");
    const card = track.querySelector(".miniCartBodyRecommendationsTrackCard");
    if (!card) {
        return;
    }

    const step = card.offsetWidth + parseFloat(getComputedStyle(track).columnGap);
    track.scrollBy({left: direction * step, behavior: "smooth"});
}

async function addMiniCartRecommendation(button) {
    document.querySelector("#miniCartContent").classList.add("loading");

    // addProduct posts the body as is, so it needs FormData
    const formData = new FormData();
    formData.append("id", button.dataset.variant);
    formData.append("quantity", 1);
    await addProduct(formData);

    await openCart();
}

// products with options and the set choose in the quick add window first, the rest add straight away
async function addMiniCartCard(button, jsonSelector) {
    if (button.hasAttribute("data-quick-add")) {
        const data = JSON.parse(button.closest("article").querySelector(jsonSelector).textContent);
        openQuickAdd(data, () => openCart());
        return;
    }

    await addMiniCartRecommendation(button);
}

/********************* Body Events *********************/

// one delegated handler for every button rendered inside the body
async function handleMiniCartBodyClick(e) {
    // recommendation arrows
    if (e.target.closest("#miniCartBodyRecommendationsHeaderNavPrev")) {
        scrollMiniCartRecommendations(-1);
        return;
    }
    if (e.target.closest("#miniCartBodyRecommendationsHeaderNavNext")) {
        scrollMiniCartRecommendations(1);
        return;
    }

    // recommendation add
    const recommendationButton = e.target.closest("button.miniCartBodyRecommendationsTrackCardButton");
    if (recommendationButton) {
        await addMiniCartCard(recommendationButton, ".miniCartBodyRecommendationsTrackCardJSON");
        return;
    }

    // remove an applied discount code
    const discountRemove = e.target.closest(".miniCartBodyDiscountCodesCodeRemove");
    if (discountRemove) {
        await removeMiniCartDiscount(discountRemove.dataset.code);
        return;
    }

    // everything below acts on a line item
    const item = e.target.closest(".miniCartBodyListItem");
    if (!item) {
        return;
    }
    const key = item.dataset.key;
    const quantity = parseInt(item.querySelector(".miniCartBodyListItemInfoActionsQuantityInput").defaultValue);

    // minus button, dropping to 0 removes the line
    if (e.target.closest(".miniCartBodyListItemInfoActionsQuantityMinus")) {
        await updateCartItem(key, quantity - 1);
        return;
    }

    // plus button
    if (e.target.closest(".miniCartBodyListItemInfoActionsQuantityPlus")) {
        await updateCartItem(key, quantity + 1);
        return;
    }

    // remove button
    if (e.target.closest(".miniCartBodyListItemInfoActionsRemove")) {
        await updateCartItem(key, 0);
        return;
    }

    // upgrade to subscription button
    const upgrade = e.target.closest(".miniCartBodyListItemInfoUpgrade");
    if (upgrade) {
        document.querySelector("#miniCartContent").classList.add("loading");
        cart = await changeCart({
            id: key,
            quantity: quantity,
            selling_plan: parseInt(upgrade.dataset.plan)
        });
        await openCart(true);
    }
}

document.querySelector("#miniCartBody").addEventListener("click", handleMiniCartBodyClick);

// best sellers in the empty cart
document.querySelector("#miniCartEmpty").addEventListener("click", async function (e) {
    const button = e.target.closest("button.miniCartEmptyProductsGridCardButton");
    if (!button) {
        return;
    }

    await addMiniCartCard(button, ".miniCartEmptyProductsGridCardJSON");
});
document.querySelector("#miniCartBodyList").addEventListener("change", handleMiniCartQuantityInput);
document.querySelector("#miniCartBodyRecommendationsTrack").addEventListener("scroll", updateMiniCartRecommendationsNav);
document.querySelector("#miniCartBodyDiscountForm").addEventListener("submit", applyMiniCartDiscount);
document.querySelector("#miniCartBodyDiscountFormInput").addEventListener("input", hideMiniCartDiscountError);

// mobile hides the discount label, so the placeholder names the field there instead
const miniCartMobileQuery = window.matchMedia("(max-width: 800px)");

function updateMiniCartDiscountPlaceholder() {
    const input = document.querySelector("#miniCartBodyDiscountFormInput");
    input.placeholder = input.dataset.placeholderDesktop;
    if (miniCartMobileQuery.matches) {
        input.placeholder = input.dataset.placeholderMobile;
    }
}

updateMiniCartDiscountPlaceholder();
miniCartMobileQuery.addEventListener("change", updateMiniCartDiscountPlaceholder);

// protection switch changes the totals and confirmation line
document.querySelector("#miniCartBodyProtectionSideSwitchInput")?.addEventListener("change", async function () {
    await openCart(true);
});

/********************* Checkout *********************/

async function miniCartCheckout(e) {
    e.currentTarget.classList.add("loading");

    // add the protection product right before leaving when the switch is on
    if (miniCartProtectionChecked()) {
        const formData = new FormData();
        formData.append("id", cartSettings.protection_variant_id);
        formData.append("quantity", 1);
        await addProduct(formData);
    }

    window.location.href = `${Shopify.routes.root}checkout`;
}

document.querySelector("#miniCartFooterCheckout").addEventListener("click", miniCartCheckout);

// swiping back from checkout restores the page from the back forward cache, mid loading and holding protection
window.addEventListener("pageshow", async function (e) {
    if (!e.persisted) {
        return;
    }

    // clear the checkout loading state
    document.querySelector("#miniCartFooterCheckout").classList.remove("loading");

    // re-fetch so openCart strips the protection added on the way out
    if (document.querySelector("#miniCart").open) {
        await openCart();
    }
});

/********************* Close Cart *********************/

// close event
document.querySelector("#miniCart").addEventListener("close", function () {
    document.querySelector("body").classList.remove("noScroll");
});

// close cart function: play the fade and slide out, then close once the slide ends
function closeCart() {
    const miniCart = document.querySelector("#miniCart");
    if (!miniCart.open || miniCart.classList.contains("closing")) {
        return;
    }

    // reduced motion turns the animations off, so there is no animationend to wait for
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        miniCart.close();
        return;
    }

    miniCart.classList.add("closing");
}

// finish closing after the slide out (other animations inside the content bubble up here too)
document.querySelector("#miniCartContent").addEventListener("animationend", function (e) {
    if (e.animationName !== "miniCartSlideOut") {
        return;
    }
    const miniCart = document.querySelector("#miniCart");
    miniCart.classList.remove("closing");
    miniCart.close();
});

// escape closes the dialog natively, so route it through closeCart to animate
document.querySelector("#miniCart").addEventListener("cancel", function (e) {
    e.preventDefault();
    closeCart();
});

// open cart event listener
document.querySelectorAll(".openCart").forEach(function (element) {
    element.addEventListener("click", function (e) {
        e.preventDefault();
        openCart();
    });
});

// close cart event listener
document.querySelectorAll(".closeCart").forEach(function (element) {
    element.addEventListener("click", closeCart);
});

// close cart on overlay click
document.querySelector("#miniCart").addEventListener("click", function (e) {
    if (e.target === this) {
        closeCart();
    }
});
